import { getItem, removeItem, setItem } from '@ms/yammer-libs-local-storage';
import { reportError } from '@ms/yammer-telemetry';
import { useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PersistStorage } from 'zustand/middleware';
import type { StoreApi } from 'zustand/vanilla';
import { createStore } from 'zustand/vanilla';

import { fetchCpsPolicies } from '../api/fetchCpsPolicies';

import type { CompliancePersistedData, ComplianceState, ComplianceStore } from './complianceStore.types';
import { complianceStorageKey, currentCpsCacheVersion, failedRequestRetryMinutes } from './complianceStore.types';

const minuteInMs = 60000;

/**
 * Shape of the data persisted to localStorage.
 * Only state fields are stored — actions and transient error state are excluded.
 */
interface PersistedComplianceState {
  readonly status: ComplianceState['status'];
  readonly policies: ComplianceState['policies'];
  readonly checkInTime: ComplianceState['checkInTime'];
  readonly requestHash: ComplianceState['requestHash'];
  readonly cacheVersion: ComplianceState['cacheVersion'];
  readonly failedRequestCheckInTime: ComplianceState['failedRequestCheckInTime'];
}

const complianceStorage: PersistStorage<PersistedComplianceState> = {
  getItem,
  setItem,
  removeItem,
};

const initialState: ComplianceState = {
  status: 'idle',
  policies: undefined,
  checkInTime: undefined,
  requestHash: undefined,
  cacheVersion: undefined,
  error: undefined,
  failedRequestCheckInTime: undefined,
};

/**
 * Check if the cache is out of date and needs to be cleared.
 */
const isCacheOutOfDate = (state: ComplianceState): boolean => {
  if (state.status !== 'fulfilled' || state.cacheVersion === undefined) {
    return false;
  }

  return state.cacheVersion !== currentCpsCacheVersion;
};

/**
 * Check if policy data should be updated based on current state.
 */
const shouldUpdatePolicyData = (state: ComplianceState): boolean => {
  const now = Date.now();

  if (state.status === 'fulfilled' && state.checkInTime !== undefined) {
    // Don't update if we haven't reached the check-in time yet
    if (state.checkInTime >= now) {
      return false;
    }
  } else if (state.status === 'rejected' && state.failedRequestCheckInTime !== undefined) {
    // Throttle retries after failures
    if (state.failedRequestCheckInTime >= now) {
      return false;
    }
  }

  return true;
};

type SetState = (partial: Partial<ComplianceStore> | ((state: ComplianceStore) => Partial<ComplianceStore>)) => void;
type GetState = () => ComplianceStore;

/**
 * Create the compliance store with persistence to localStorage.
 */
const createComplianceStore = () =>
  createStore<ComplianceStore>()(
    persist(
      (set: SetState, get: GetState) => ({
        ...initialState,

        setPending: () => {
          set({ status: 'pending' });
        },

        setFulfilled: (data: CompliancePersistedData) => {
          set({
            status: 'fulfilled',
            policies: data.policies,
            checkInTime: data.checkInTime,
            requestHash: data.requestHash,
            cacheVersion: data.cacheVersion,
            error: undefined,
            failedRequestCheckInTime: undefined,
          });
        },

        setRejected: (error: unknown, checkInTime: number) => {
          set({
            status: 'rejected',
            error,
            failedRequestCheckInTime: checkInTime,
          });
        },

        clear: () => {
          set(initialState);
        },

        initialize: async (host: string, _initiator?: string) => {
          const state = get();

          if (isCacheOutOfDate(state)) {
            set(initialState);
          } else if (!shouldUpdatePolicyData(state)) {
            return;
          }

          set({ status: 'pending' });

          const previousRequestHash = get().requestHash;
          const previousPolicies = get().policies;

          try {
            const response = await fetchCpsPolicies(host, previousRequestHash);

            const checkInMinutes = parseInt(response.checkInTime, 10) || 60;
            const newCheckInTime = Date.now() + checkInMinutes * minuteInMs;

            let updatedPolicies = response.policies;
            if (response.policiesHash && response.policiesHash === previousRequestHash && previousPolicies) {
              updatedPolicies = previousPolicies;
            }

            set({
              status: 'fulfilled',
              policies: updatedPolicies,
              checkInTime: newCheckInTime,
              requestHash: response.policiesHash,
              cacheVersion: currentCpsCacheVersion,
              error: undefined,
              failedRequestCheckInTime: undefined,
            });
          } catch (error) {
            reportError({ error });
            const failedCheckInTime = Date.now() + failedRequestRetryMinutes * minuteInMs;
            set({
              status: 'rejected',
              error,
              failedRequestCheckInTime: failedCheckInTime,
            });
          }
        },
      }),
      {
        name: complianceStorageKey,
        storage: complianceStorage,
        partialize: (state: ComplianceStore): PersistedComplianceState => ({
          // Only persist these fields to localStorage
          status: state.status === 'fulfilled' ? 'fulfilled' : 'idle',
          policies: state.policies,
          checkInTime: state.checkInTime,
          requestHash: state.requestHash,
          cacheVersion: state.cacheVersion,
          failedRequestCheckInTime: state.failedRequestCheckInTime,
        }),
      }
    )
  );

/**
 * Vanilla Zustand store for use in non-React contexts (e.g., bootstrap).
 */
export const complianceStore: StoreApi<ComplianceStore> = createComplianceStore();

/**
 * React hook version of the compliance store.
 */
export const useComplianceStore = <T>(selector: (state: ComplianceStore) => T): T =>
  useStore(complianceStore, selector);
